<template>
  <div
    v-if="showWidget"
    class="social-proof-widget-component"
  >
    <dashboard-widget-header
      v-if="config.title"
      :icon="widgetIcon"
      :title="config.title"
    />
    <div class="social-proof-content">
      <button
        v-if="hasData"
        class="social-proof-action"
        @click="routeToSocialPage"
      >
        <div class="social-proof-data">
          <div class="studying-now">
            <p class="header">
              Members Studying Now
            </p>
            <p class="total">
              {{ formatSocialData(totalActiveUsersNow) }}
            </p>
          </div>
          <div class="total-data">
            <div class="segment">
              <p class="label">
                Members
              </p>
              <p class="data">
                {{ formatSocialData(totalActiveUsers) }}
              </p>
            </div>
            <div class="segment">
              <p class="label">
                Answers
              </p>
              <p class="data">
                {{ formatSocialData(totalAnswers) }}
              </p>
            </div>
            <div class="segment">
              <p class="label">
                Today
              </p>
              <p class="data">
                {{ formatSocialData(totalAnswersToday) }}
              </p>
            </div>
          </div>
        </div>
        <div
          class="social-proof-image"
          role="img"
          :style="{ backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : '' }"
        />
        <div class="social-proof-arrow">
          <icon
            name="mastery-arrow-right"
            variant="white"
            class="arrow-icon"
          />
        </div>
      </button>
      <loading-bar v-else />
    </div>
  </div>
</template>

<script>
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
import Icon from '@components/singletons/icon';
import LoadingBar from '@components/singletons/loading-bar';

export default {
  name: 'SocialProofWidget',
  components: { DashboardWidgetHeader, Icon, LoadingBar },
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      hasData: false,
      showWidget: false,
      totalActiveUsers: null,
      totalActiveUsersNow: null,
      totalAnswers: null,
      totalAnswersToday: null,
    };
  },
  computed: {
    backgroundImageUrl() {
      if (this.config) {
        const attachment = this.$store.getters['attachments/get'][this.config.config.socialProofAttachmentId];
        return attachment?.url;
      }
    },
    widgetIcon() {
      if (this.config) {
        return this.$store.getters['attachments/get'][this.config.iconAttachmentId];
      }
    },
  },
  mounted() {
    let widgetHidden = true;
    const user = this.$store.getters['session/currentUser'];
    if (user && user.previewer) {
      widgetHidden = false;
    } else {
      widgetHidden = this.$apptimize.checkExperiments({
        experimentName: 'Social Proof Widget',
        arrName: 'alwaysHiddenWidgets',
        defaultVal: [],
        arrValue: this.config.id,
        matcher: /Social Proof Widget/g,
      });
    }
    if (!widgetHidden) {
      this.showWidget = true;
      this.$store.getters['session/ajax'].get('/social_proof').then(({ data }) => {
        this.setSocialData(data);
      });
      if (this.config?.config?.socialProofAttachmentId) {
        this.$store.dispatch('attachments/fetch', { ids: [this.config.config.socialProofAttachmentId] });
      }
    }

  },
  methods: {
    formatSocialData(num) {
      const numified = Number(num);
      if (isNaN(numified)) {
        return '';
      }
      const billion = 1000000000;
      const million = 1000000;
      const hundothou = 100000;
      const thou = 1000;
      if (numified >= billion ) {
        return `${(numified / billion).toFixed(1)}b`;
      } else if (numified >= million) {
        return `${(numified / million).toFixed(1)}m`;
      } else if (numified >= hundothou) {
        const total = String(num / thou);
        return `${total.substring(0, 3)}k`;
      } else {
        return numified.toLocaleString();
      }
    },
    routeToSocialPage() {
      this.$analytics.events.viewedWidget(this.config, null, null, this.dashboard);
      this.$router.push({
        name: 'social',
        query: {
          widget: this.config.id,
        },
      });
    },
    setSocialData(data) {
      this.hasData = true;
      this.totalActiveUsers = data.totalActiveUsers;
      this.totalActiveUsersNow = data.totalActiveUsers_1h;
      this.totalAnswers = data.totalQuestionsAnswered;
      this.totalAnswersToday = data.totalQuestionsAnswered_24h;
    },
  },
};
</script>

<style lang="scss" scoped>
.social-proof-widget-component {
  @include padded-widget;

  .social-proof-content {
    align-items: center;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(34, 34, 34, 0.16);
    display: flex;
    height: rem-calc(216);

    .social-proof-action {
      align-items: center;
      background: transparent;
      box-sizing: border-box;
      display: flex;
      height: 100%;
      padding: 0;
      position: relative;
      text-align: left;
      width: 100%;

      .social-proof-data {
        box-sizing: border-box;
        display: flex;
        flex: 1;
        flex-direction: column;
        height: 100%;
        max-width: 272px;
        padding: rem-calc(64) rem-calc(32) rem-calc(16);
        z-index: 10;

        p {
          margin: 0;
        }

        .studying-now {
          .header {
            color: #767676;
            font-size: rem-calc(12);
            font-weight: $bold;
            text-transform: uppercase;
          }

          .total {
            @include charter-black;
            font-size: rem-calc(30);
          }
        }

        .total-data {
          display: flex;
          justify-content: space-between;
          margin-top: auto;
          width: 100%;

          .segment {
            .label {
              color: #767676;
              font-size: rem-calc(12);
              font-weight: $bold;
              text-transform: uppercase;
            }

            .data {
              color: #00dbfb;
              font-size: rem-calc(14);
              font-weight: $bold;
            }
          }
        }
      }

      .social-proof-image {
        background-position: center;
        background-size: cover;
        height: 100%;
        max-width: rem-calc(500);
        position: absolute;
        width: 75%;
        right: 30px;
      }

      .social-proof-arrow {
        align-items: center;
        background-color: #000000;
        border-radius: 100%;
        display: flex;
        justify-content: center;
        height: rem-calc(32);
        margin-left: auto;
        margin-right: rem-calc(16);
        width: rem-calc(32);
        z-index: 10;

        .arrow-icon {
          width: rem-calc(16);
        }
      }
    }

    @include breakpoint(small) {
      .social-proof-action {
        .social-proof-image {
          right: 0;
          border-radius: 0 8px 8px 0;
        }
      }
    }
  }
}

</style>
